import {
  Box,
  Typography,
  useTheme,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { type GridColumns, type GridValidRowModel } from '@mui/x-data-grid';
import { CustomDataGrid } from 'components';
import Buttons from 'components/elements/Buttons';
import { CurrencyFormater } from 'components/helpers/commonFunctions/CurrencyFormater';
import { strings } from 'i18n/Strings';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getBankFileReport, exportBankFileReport } from 'service';
import { colorTheme } from 'theme';
import { dateFormat } from 'utils/GeneralUtil';
import Header from 'components/Header';
import { reportFilterBy } from 'utils/Constants';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';

interface ITransaction {
  actionType: string;
  amount: string;
  bankFileId: number;
  creditorName: string;
  currentStatus: string;
  currentType: string;
  fileName: string;
  fileUploadedDate: string;
  logCreatedAt: string;
  logId: number;
  recordCreatedAt: string;
  transactionReference: string;
}

const BankFileRecordReport = (): JSX.Element => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const dispatch = useDispatch();
  const [report, setReport] = useState<ITransaction[]>([]);
  const [filterBy, setFilterBy] = useState<string>(reportFilterBy[0].value);

  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [isLoader, setIsLoader] = useState(false);
  const [pageCount, setPageCount] = useState(null);
  const pageInfo = useSelector((state) => state?.pagination?.bankReport);
  type GridRowData = Record<string, unknown>;

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'fileName',
      width: 400,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          File Name
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip title={row.fileName}>
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {row.fileName}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'amount',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Amount
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {CurrencyFormater(row.amount)}
        </Typography>
      ),
    },
    {
      field: 'paymentType',
      width: 160,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.creditOrDebit}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.paymentType}
        </Typography>
      ),
    },
    {
      field: 'transactionReference',
      width: 250,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Current Transaction Reference
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.transactionReference}
        </Typography>
      ),
    },
    {
      field: 'creditorName',
      width: 160,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Creditor Name
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.creditorName}
        </Typography>
      ),
    },
    {
      field: 'debtorName',
      width: 160,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Debtor Name
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.debtorName}
        </Typography>
      ),
    },
    {
      field: 'bankFileId',
      width: 160,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Bank File ID
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.bankFileId}
        </Typography>
      ),
    },

    {
      field: 'currentStatus',
      width: 160,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Current Status
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.currentStatus}
        </Typography>
      ),
    },
    {
      field: 'currentType',
      width: 160,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Current Type
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.currentType}
        </Typography>
      ),
    },
    {
      field: 'recordCreatedAt',
      width: 160,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Record Created At
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.recordCreatedAt ? dateFormat(row.recordCreatedAt) : '-'}
        </Typography>
      ),
    },
    {
      field: 'reconciledAt',
      width: 160,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Reconciled At
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.reconciledAt ? dateFormat(row.reconciledAt) : '-'}
        </Typography>
      ),
    },
    {
      field: 'orderPlacedAt',
      width: 160,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Order Placed At
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.orderPlacedAt ? dateFormat(row.orderPlacedAt) : '-'}
        </Typography>
      ),
    },
    {
      field: 'statusTypeUpdatedAt',
      width: 200,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Status Type Updated At
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.statusTypeUpdatedAt ? dateFormat(row.statusTypeUpdatedAt) : '-'}
        </Typography>
      ),
    },
  ];

  const getReport = (): void => {
    getBankFileReport(
      Number(pageInfo?.page) + 1,
      pageInfo?.pageSize,
      date,
      filterBy,
    )
      .then((reportList) => {
        setReport(reportList?.rows || []);
        setPageCount(reportList?.count || 0);
      })
      .catch((err: Error) => {
        throw err;
      });
  };

  useEffect(() => {
    getReport();
  }, [pageInfo?.page, pageInfo?.pageSize, date, filterBy]);

  const handleExport = async (): Promise<void> => {
    try {
      setIsLoader(true);
      const url = await exportBankFileReport(date, filterBy);
      const link = document.createElement('a');
      link.href = url as string;
      link.download = `${filterBy}_${date}_export.xlsx`;
      link.click();
      dispatch(
        setSnackBarSuccess({
          snackBarMessage: strings.displayText.bankFileReportExportSuccess,
        }),
      );
    } catch (error) {
      console.log('Error', error);
      dispatch(
        setSnackBarFailed({
          snackBarMessage: strings.displayText.bankFileReportExportFailed,
        }),
      );
    } finally {
      setIsLoader(false);
    }
  };

  return (
    <Box>
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Header title="" />
        <Box display="flex" flexDirection="row" justifyContent="flex-end">
          <FormControl fullwidth>
            <InputLabel id="maritalStatus">Filter By</InputLabel>
            <Select
              label={'Filter By'}
              name={'filterBy'}
              required={true}
              value={filterBy}
              onChange={(e) => {
                setFilterBy(e.target.value);
              }}
              style={{ width: '250px' }}
            >
              {reportFilterBy.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            type="date"
            value={date}
            sx={{ mx: 2 }}
            placeholder=""
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setDate(e.target.value);
            }}
            InputLabelProps={{ shrink: true }}
          />

          <Buttons
            variant="contained"
            color="primary"
            disabled={isLoader}
            style={{ margin: 'auto' }}
            onClick={() => {
              void handleExport();
            }}
            endIcon={isLoader ? <CircularProgress size={15} /> : null}
          >
            {strings.displayText.export}
          </Buttons>
        </Box>
      </Box>
      <CustomDataGrid
        columns={columns}
        rows={report}
        getRowId={(row: GridRowData) => row.id}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        currentPage="bankReport"
        pageCount={pageCount}
      />
    </Box>
  );
};

export default BankFileRecordReport;
